import { fetchQuery, graphql } from 'react-relay';

import { APIConfig } from 'src/config';
import {
  DATA_CONSUMED_KEY, DATA_GENERATED_KEY, DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES,
  ELEC_ENERGY_CONSUMED, ELEC_ENERGY_GENERATED,
  METER_LIST_PAGE_SIZE, TRADE_RULE_LIST_PAGE_SIZE,
} from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import logger from 'src/util/logger';
import {
  calculateTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/batchCalculator';

import { PropertyShowLoadingActions } from '../reducer/constants';

// GraphQL Queries
// Query 1: Meters data. Paginated to handle properties with many meters.
const metersQuery = graphql`
query dataBatchingMetersQuery(
  $uuid: ID!,
  $pageSize: Int,
  $after: String
) {
  property(uuid:$uuid) {
    meters(first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          identifier
          externalIdentifier
          active { start finish }
          title
          tradePointId
          property {
            title
          }
          primaryBillingPoint {
            identifier
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 2: Rules data. Paginated to handle meters with many rules.
const rulesQuery = graphql`
query dataBatchingRulesQuery(
  $meterId: ID!,
  $start: Timestamp!,
  $finish: Timestamp!,
  $pageSize: Int,
  $after: String
) {
  meter(uuid: $meterId) {
    uuid
    rules(start: $start, finish: $finish, first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          tradeType
          buyer {
            userId
            user {
              uuid
              email
              givenName
              familyName
            }
            tradePoint {
              uuid
              type
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          seller {
            userId
            user {
              uuid
              email
              givenName
              familyName
            }
            tradePoint {
              uuid
              type
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          state
          clauses {
            edges {
              node {
                price
                ignorePublicHolidays
                monthsOfYear
                daysOfWeek
                timesOfDay {
                  start { hours minutes }
                  finish { hours minutes }
                }
              }
            }
          }
          proposedAt
          proposedBy {
            uuid
            email
            givenName
            familyName
          }
          acceptedAt
          acceptedBy {
            uuid
            email
            givenName
            familyName
          }
          start
          finish
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 3: Meter data (consumed/generated) - unified query with metric parameter
const meterDataQuery = graphql`
query dataBatchingMeterDataQuery(
  $meterId: ID!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $aggregation: String!,
  $timeZone: String!,
  $metric: String!
) {
  meter(uuid: $meterId) {
    uuid
    calculatedData(start: $batchStart, finish: $batchFinish, aggregation: $aggregation, timeZone: $timeZone, metric: $metric) {
      aggregation
      metric {
        identifier
      }
      timeZone
      timeRange {
        start
        finish
      }
      data {
        timestamp
        value
        flags {
          identifier
          description
          Valid
        }
      }
    }
  }
}
`;

// Query 4: Trade set summary for a meter.
const meterTradeSetSummaryQuery = graphql`
query dataBatchingMeterTradeSetSummaryQuery(
  $meterId: ID!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $timeZone: String!,
  $tradeAggregation: String!
) {
  meter(uuid: $meterId) {
    uuid
    tradeSetSummary(start: $batchStart, finish: $batchFinish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
      key {
        tradePointId
        ruleId
        direction
        type
      }
      data {
        range { start finish }
        buyerTradePointIds
        sellerTradePointIds
        directions
        types
        value
        volume
        averagePrice
        tradeCount
        intervalCount
      }
    }
  }
}
`;

/**
 * Calculate total rules count across all meters
 * @param {Array} rulesData - Array of rules data objects with rules property
 * @returns {number} Total count of rules across all meters
 */
export const getTotalRulesCount = (rulesData) => {
  if (!rulesData || !Array.isArray(rulesData)) {
    return 0;
  }

  return rulesData.reduce((sum, meterRules) => {
    const rulesCount = meterRules?.rules?.edges?.length || 0;
    return sum + rulesCount;
  }, 0);
};

/**
 * Helper function to merge data from multiple property data batches
 * This function makes the deep cloning and merging logic testable
 * @param {object} initialData - Initial property data to use as the base
 * @param {Array} batches - Array of data batches to merge
 * @returns {object} Merged property data
 */
export const mergePropertyDataBatches = (initialData, batches) => {
  if (!initialData || !batches || !batches.length) {
    logger.error('No initialData or batches provided to merge');
    return initialData;
  }

  logger.log(`Merging ${batches.length} property data batches`);

  try {
    let mergedData = {};
    try {
      mergedData = JSON.parse(JSON.stringify(initialData));
    } catch (cloneError) {
      logger.error('Error cloning initialData:', cloneError);
    }

    // Create map of meters by UUID for efficient lookups
    const metersMap = new Map();

    // Initialize meters map from merged data
    if (mergedData?.property?.meters?.edges) {
      mergedData.property.meters.edges.forEach((edge) => {
        const meter = edge?.node;
        if (meter?.uuid) {
          metersMap.set(meter.uuid, meter);
        }
      });
    }

    // Now merge all batches
    batches.forEach((batch) => {
      if (!batch?.property?.meters?.edges) return;

      batch.property.meters.edges.forEach((meterEdge) => {
        const batchMeterNode = meterEdge?.node;
        if (!batchMeterNode?.uuid) return;

        // Get or create meter in merged data
        if (!metersMap.has(batchMeterNode.uuid)) {
          metersMap.set(batchMeterNode.uuid, { ...batchMeterNode });
        }

        const mergedMeterNode = metersMap.get(batchMeterNode.uuid);

        // Merge rules
        if (batchMeterNode.rules?.edges) {
          if (!mergedMeterNode.rules) {
            mergedMeterNode.rules = { edges: [] };
          }

          // Create a Set of existing rule IDs to avoid duplicates when merging batches
          const existingRuleIds = new Set(
            mergedMeterNode.rules.edges?.map((e) => e?.node?.uuid) || [],
          );

          // Merge rules from this batch, skipping any that already exist in merged data
          batchMeterNode.rules.edges.forEach((ruleEdge) => {
            const rule = ruleEdge?.node;
            if (rule?.uuid && !existingRuleIds.has(rule.uuid)) {
              mergedMeterNode.rules.edges.push(ruleEdge);
              existingRuleIds.add(rule.uuid);
            }
          });
        }

        // Merge data consumed
        if (batchMeterNode[DATA_CONSUMED_KEY]?.data) {
          mergedMeterNode[DATA_CONSUMED_KEY] = batchMeterNode[DATA_CONSUMED_KEY];
        }

        // Merge data generated
        if (batchMeterNode[DATA_GENERATED_KEY]?.data) {
          mergedMeterNode[DATA_GENERATED_KEY] = batchMeterNode[DATA_GENERATED_KEY];
        }

        // Merge trade set summary
        if (Array.isArray(batchMeterNode.tradeSetSummary)) {
          if (!mergedMeterNode.tradeSetSummary) {
            mergedMeterNode.tradeSetSummary = [];
          }

          // Create a map of existing trades by key for efficient lookups
          const existingTradeMap = new Map();
          mergedMeterNode.tradeSetSummary.forEach((trade) => {
            const key = trade.key || {};
            const tradeKey = `${key.tradePointId}_${key.ruleId}_${key.direction}_${key.type}`;
            existingTradeMap.set(tradeKey, trade);
          });

          batchMeterNode.tradeSetSummary.forEach((trade) => {
            const key = trade.key || {};
            const tradeKey = `${key.tradePointId}_${key.ruleId}_${key.direction}_${key.type}`;

            if (existingTradeMap.has(tradeKey)) {
              // Merge data arrays for existing trade key
              const existingTrade = existingTradeMap.get(tradeKey);
              if (Array.isArray(existingTrade.data) && Array.isArray(trade.data)) {
                // Combine the data arrays from both batches
                existingTrade.data = [...existingTrade.data, ...trade.data];
              }
            } else {
              // Add new trade key
              mergedMeterNode.tradeSetSummary.push(trade);
              existingTradeMap.set(tradeKey, trade);
            }
          });
        }
      });
    });

    // Rebuild the meters edges array from the map
    if (!mergedData?.property) {
      mergedData.property = {};
    }
    if (!mergedData.property.meters) {
      mergedData.property.meters = {};
    }

    mergedData.property.meters.edges = Array.from(metersMap.values()).map((meter) => ({
      node: meter,
    }));

    logger.log(`Successfully merged ${batches.length} property data batches`);
    return mergedData;
  } catch (error) {
    logger.error('Error merging property data batches:', error);
    return initialData;
  }
};

/**
 * Get meters for the property with pagination support
 * @param {object} args - Arguments for fetching meters
 * @param {string} args.propertyId - Property ID
 * @param {object} args.environment - Relay environment
 * @param {number} [args.pageSize=METER_LIST_PAGE_SIZE] - Number of meters to fetch per page
 * @returns {Promise<Array>} Promise that resolves to array of meter objects
 */
export const getMeters = async (args) => {
  const {
    propertyId,
    environment,
    pageSize = METER_LIST_PAGE_SIZE,
  } = args;

  const allMeters = [];
  let hasNextPage = true;
  let afterCursor = '';

  while (hasNextPage) {
    const variables = {
      uuid: propertyId,
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await queryWithRetry({
        queryFn: () => {
          let resolvePromise;
          let rejectPromise;

          const promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
          });

          const subscription = fetchQuery(environment, metersQuery, variables).subscribe({
            next: (result) => {
              resolvePromise(result);
            },
            error: (error) => {
              rejectPromise(error);
            },
          });

          promise.unsubscribe = () => subscription.unsubscribe();

          return promise;
        },
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `Fetching meters for property ${propertyId}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.property?.meters?.edges) {
        logger.warn('No meters data returned');
        break;
      }

      const pageMeters = data.property.meters.edges.map((edge) => edge.node);
      allMeters.push(...pageMeters);
      logger.log(`Stage 1: Fetched ${pageMeters.length} meters (total: ${allMeters.length})`);

      // Check if there are more pages
      hasNextPage = data.property.meters.pageInfo?.hasNextPage || false;
      afterCursor = data.property.meters.pageInfo?.endCursor || '';
    } catch (error) {
      logger.error('Error fetching meters page:', error);
      break; // Exit pagination loop on error
    }
  }

  logger.log(`getMeters retrieved ${allMeters.length} meters`);
  return allMeters;
};

/**
 * Get trade rules for a meter with pagination support
 * @param {object} args - Arguments for fetching rules
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - start time (Luxon DateTime)
 * @param {object} args.finish - finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {number} [args.pageSize=TRADE_RULE_LIST_PAGE_SIZE] - Number of rules to fetch per page
 * @returns {Promise<object>} Promise that resolves to meter with rules data
 */
export const getRules = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    pageSize = TRADE_RULE_LIST_PAGE_SIZE,
  } = args;

  logger.log(`Stage 2: Fetching rules for meter ${meterId}`);

  const allRules = [];
  let hasNextPage = true;
  let afterCursor = '';

  while (hasNextPage) {
    const variables = {
      meterId,
      start: Math.floor(start.toSeconds()),
      finish: Math.floor(finish.toSeconds()),
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await queryWithRetry({
        queryFn: () => {
          let resolvePromise;
          let rejectPromise;

          const promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
          });

          const subscription = fetchQuery(environment, rulesQuery, variables).subscribe({
            next: (result) => {
              resolvePromise(result);
            },
            error: (error) => {
              rejectPromise(error);
            },
          });

          promise.unsubscribe = () => subscription.unsubscribe();

          return promise;
        },
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `Fetching rules for meter ${meterId}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.meter?.rules?.edges) {
        logger.warn(`No rules data returned for ${meterId}`);
        break;
      }

      const pageRules = data.meter.rules.edges;
      allRules.push(...pageRules);
      logger.log(`Stage 2: Fetched ${pageRules.length} rules (total: ${allRules.length}) for meter ${meterId}`);

      // Check if there are more pages
      hasNextPage = data.meter.rules.pageInfo?.hasNextPage || false;
      afterCursor = data.meter.rules.pageInfo?.endCursor || '';
    } catch (error) {
      logger.error(`Error fetching rules page for meter ${meterId}:`, error);
      break; // Exit pagination loop on error
    }
  }

  logger.log(`Stage 2: Found ${allRules.length} total rules for meter ${meterId}`);
  return { uuid: meterId, rules: { edges: allRules } };
};

/**
 * Fetch meter data (consumed or generated) for a single meter.
 * @param {object} args - Arguments for fetching meter data
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - start time (Luxon DateTime)
 * @param {object} args.finish - batch end time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {string} args.metric -
 * Metric type (e.g., "elec_energy_consumed" or "elec_energy_generated")
 * @param {string} args.historianAggregation - Aggregation level
 * @param {string} args.timezone - Timezone
 * @returns {Promise<object>} Promise that resolves to meter with data
 */
export const getMeterData = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    metric,
    historianAggregation,
    timezone,
  } = args;

  const isConsumed = metric === ELEC_ENERGY_CONSUMED;
  const stage = isConsumed ? 3 : 4;
  const key = isConsumed ? DATA_CONSUMED_KEY : DATA_GENERATED_KEY;

  const variables = {
    meterId,
    batchStart: Math.floor(start.toSeconds()),
    batchFinish: Math.floor(finish.toSeconds()),
    aggregation: historianAggregation,
    timeZone: timezone,
    metric,
  };

  logger.log(`Stage ${stage}: Fetching ${metric} data for meter ${meterId}`);

  try {
    const data = await queryWithRetry({
      queryFn: () => {
        let resolvePromise;
        let rejectPromise;

        const promise = new Promise((resolve, reject) => {
          resolvePromise = resolve;
          rejectPromise = reject;
        });

        const subscription = fetchQuery(environment, meterDataQuery, variables).subscribe({
          next: (result) => {
            resolvePromise(result);
          },
          error: (error) => {
            rejectPromise(error);
          },
        });

        promise.unsubscribe = () => subscription.unsubscribe();

        return promise;
      },
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching ${metric} data for meter ${meterId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    if (!data?.meter?.calculatedData) {
      logger.warn(`No ${metric} data returned for ${meterId}`);
      return { uuid: meterId, [key]: { data: [] } };
    }

    const dataCount = data.meter.calculatedData?.data?.length || 0;
    logger.log(`Stage ${stage}: Found ${dataCount} ${metric} data points for meter ${meterId}`);
    // Deep clone to ensure mutability for merging
    const clonedData = JSON.parse(JSON.stringify(data.meter.calculatedData));
    return { uuid: meterId, [key]: clonedData };
  } catch (error) {
    logger.error(`Error fetching ${metric} data for meter ${meterId}:`, error);
    return { uuid: meterId, [key]: { data: [] } };
  }
};

/**
 * Fetch trade set summary for a single meter within batch time range
 * @param {object} args - Arguments for fetching trade set summary
 * @param {string} args.meterId - Meter ID
 * @param {object} args.start - start time (Luxon DateTime)
 * @param {object} args.finish - finish time (Luxon DateTime)
 * @param {object} args.environment - Relay environment
 * @param {string} args.tradeAggregation - Aggregation level
 * @param {string} args.timezone - Timezone
 * @returns {Promise<object>} Promise that resolves to meter with trade set summary
 */
export const getTradeSetSummaryForMeter = async (args) => {
  const {
    meterId,
    start,
    finish,
    environment,
    tradeAggregation,
    timezone,
  } = args;

  const variables = {
    meterId,
    batchStart: Math.floor(start.toSeconds()),
    batchFinish: Math.floor(finish.toSeconds()),
    tradeAggregation,
    timeZone: timezone,
  };

  logger.log(`Stage 5: Fetching trade set summary for ${JSON.stringify(variables)}`);

  try {
    const data = await queryWithRetry({
      queryFn: () => {
        let resolvePromise;
        let rejectPromise;

        const promise = new Promise((resolve, reject) => {
          resolvePromise = resolve;
          rejectPromise = reject;
        });

        const subscription = fetchQuery(
          environment,
          meterTradeSetSummaryQuery,
          variables,
        ).subscribe({
          next: (result) => {
            resolvePromise(result);
          },
          error: (error) => {
            rejectPromise(error);
          },
        });

        promise.unsubscribe = () => subscription.unsubscribe();

        return promise;
      },
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching trade set summary for meter ${meterId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    if (!data?.meter?.tradeSetSummary) {
      logger.warn(`No trade set summary returned for ${JSON.stringify(variables)}`);
      return { uuid: meterId, tradeSetSummary: [] };
    }

    const tradeSetSummary = data.meter.tradeSetSummary || [];
    const tradeSetSummaryCount = tradeSetSummary.length;
    // Clone data to avoid mutating Relay cache during merge operations
    const clonedData = JSON.parse(JSON.stringify(data.meter.tradeSetSummary || []));
    logger.log(`Stage 5: Found ${tradeSetSummaryCount} trade set summary items for ${JSON.stringify(variables)}`);
    return { uuid: meterId, tradeSetSummary: clonedData };
  } catch (error) {
    logger.error(`Error fetching trade set summary for ${JSON.stringify(variables)}:`, error);
    return { uuid: meterId, tradeSetSummary: [] };
  }
};

/**
 * Merges meter data with rules, consumed, generated, and trade set summary data
 * @param {Array} meters - Array of meter objects from stage 1
 * @param {Array} rulesData - Array of meter objects with rules from stage 2
 * @param {Array} consumedData - Array of meter objects with consumed data from stage 3
 * @param {Array} generatedData - Array of meter objects with generated data from stage 4
 * @param {Array} tradeSummaryData - Array of meter objects with trade set summary from stage 5
 * @returns {object} Merged data in the expected format
 */
export const mergeBatchedData = (
  meters,
  rulesData,
  consumedData,
  generatedData,
  tradeSummaryData,
) => {
  // Guard clauses for parameters
  const validMeters = Array.isArray(meters) ? meters : [];
  const validRulesData = Array.isArray(rulesData) ? rulesData : [];
  const validConsumedData = Array.isArray(consumedData) ? consumedData : [];
  const validGeneratedData = Array.isArray(generatedData) ? generatedData : [];
  const validTradeSummaryData = Array.isArray(tradeSummaryData) ? tradeSummaryData : [];

  logger.log(`Merging five-stage data: ${validMeters.length} meters, ${validRulesData.length} rules, ${validConsumedData.length} consumed, ${validGeneratedData.length} generated, ${validTradeSummaryData.length} summary`);

  // Create maps for efficient lookups
  const rulesByMeterId = new Map();
  const consumedByMeterId = new Map();
  const generatedByMeterId = new Map();
  const summaryByMeterId = new Map();

  validRulesData.forEach((meterRules) => {
    if (meterRules.uuid) {
      rulesByMeterId.set(meterRules.uuid, meterRules.rules);
    }
  });

  validConsumedData.forEach((meterConsumed) => {
    if (meterConsumed.uuid) {
      consumedByMeterId.set(meterConsumed.uuid, meterConsumed[DATA_CONSUMED_KEY]);
    }
  });

  validGeneratedData.forEach((meterGenerated) => {
    if (meterGenerated.uuid) {
      generatedByMeterId.set(meterGenerated.uuid, meterGenerated[DATA_GENERATED_KEY]);
    }
  });

  validTradeSummaryData.forEach((meterSummary) => {
    if (meterSummary.uuid) {
      summaryByMeterId.set(meterSummary.uuid, meterSummary.tradeSetSummary);
    }
  });

  // Merge data for each meter
  const mergedMeters = validMeters.map((meter) => {
    const meterId = meter.uuid;
    return {
      ...meter,
      rules: rulesByMeterId.get(meterId) || { edges: [] },
      [DATA_CONSUMED_KEY]: consumedByMeterId.get(meterId) || { data: [] },
      [DATA_GENERATED_KEY]: generatedByMeterId.get(meterId) || { data: [] },
      tradeSetSummary: summaryByMeterId.get(meterId) || [],
    };
  });

  return {
    property: {
      meters: {
        edges: mergedMeters.map((meter) => ({ node: meter })),
      },
    },
  };
};

/**
 * Get the trade set summary for a single batch and merge the data
 * Meters, rules, consumed, and generated data are fetched separately outside batches
 * @param {object} args - Arguments for processing the batch
 * @returns {Promise<object>} Promise that resolves to merged batch data
 */
export const getBatchedTradesetSummary = async (args) => {
  const {
    meters,
    rulesData,
    consumedData,
    generatedData,
    batchStart,
    batchFinish,
    environment,
    tradeAggregation,
    timezone,
    onStageComplete,
  } = args;

  try {
    if (!meters || meters.length === 0) {
      logger.log('No meters provided, returning empty data');
      return mergeBatchedData([], [], [], [], []);
    }

    // Get trade set summary for each meter (only batched data)
    const tradeSummaryPromises = meters.map((meter) => getTradeSetSummaryForMeter({
      meterId: meter.uuid,
      start: batchStart,
      finish: batchFinish,
      environment,
      tradeAggregation,
      timezone,
    }));

    const tradeSummaryData = await Promise.all(tradeSummaryPromises);

    if (onStageComplete) {
      onStageComplete(1, 1, `Loaded trade set summary for ${tradeSummaryData.length} meters`);
    }

    // Merge all data
    const mergedData = mergeBatchedData(
      meters,
      rulesData,
      consumedData,
      generatedData,
      tradeSummaryData,
    );

    return mergedData;
  } catch (error) {
    logger.error('Error in batch processing:', error);
    return mergeBatchedData([], [], [], [], []);
  }
};

/**
 * Stage 5: Fetch trade set summary using batched time ranges.
 * Used when consumed and generated data are already fetched.
 * @param {object} args - Arguments for the fetch operation
 * @param {Array} args.meters - Array of meter objects
 * @param {Array} args.rulesData - Array of rules data per meter
 * @param {Array} args.consumedData - Array of consumed meter data
 * @param {Array} args.generatedData - Array of generated meter data
 * @param {object} args.dateSpan - Date span with start and finish properties (Luxon DateTimes)
 * @param {string} args.timezone - Timezone to use for data
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {string} args.historianAggregation - Aggregation level for historian data
 * @param {string} args.tradeAggregation - Aggregation level for trade data
 * @param {object} args.initialData - Initial data to merge batches with
 * @param {Function} args.onDataReceived - Callback when all data is received and merged
 * @param {Function} args.onError - Callback when an error occurs
 * @param {Function} args.stateDispatchFunc - Function to dispatch loading state actions
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 */
export const getTradeSetSummaries = async (args) => {
  const {
    meters,
    rulesData,
    consumedData,
    generatedData,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    stateDispatchFunc,
    cancelledRef,
  } = args;

  try {
    // Check for cancellation before starting trade set summaries
    if (cancelledRef?.current) {
      logger.log('Trade set summary fetch cancelled before starting');
      return;
    }

    const { start, finish } = dateSpan || {};

    // Calculate batch size based on rules count and aggregation
    const totalRulesCount = getTotalRulesCount(rulesData);

    const batchCalculationResult = calculateTradeSetSummaryBatchSize({
      start,
      finish,
      aggregation: historianAggregation,
      ruleCount: totalRulesCount,
    });

    const timeBatches = generateBatchTimeRanges({
      start,
      finish,
      batchSizeHours: batchCalculationResult.batchSizeHours,
    });

    const batchCount = timeBatches.length;

    // Notify stage 5: starting trade set summary
    if (stateDispatchFunc) {
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
        batchesLoaded: 0,
        batchesTotal: batchCount,
      });
    }

    // Stage 5: Fetch trade set summary using time batches
    logger.log(`Processing ${batchCount} time batch(es) with batch size: ${batchCalculationResult.batchSizeHours} hours`);

    // Track batch completion for progress updates
    let batchesCompleted = 0;

    // Process each batch with pre-fetched meters, rules, consumed, and generated data
    const batchPromises = timeBatches.map((batch, index) => getBatchedTradesetSummary({
      meters,
      rulesData,
      consumedData,
      generatedData,
      batchStart: batch.start,
      batchFinish: batch.finish,
      environment,
      tradeAggregation,
      timezone,
      onStageComplete: (stage, totalStages, message) => {
        logger.log(`Batch ${index + 1}/${batchCount} - Stage ${stage}/${totalStages}: ${message}`);
      },
    }).then((result) => {
      // Notify batch completion via reducer dispatch
      batchesCompleted += 1;
      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
          batchesLoaded: batchesCompleted,
          batchesTotal: batchCount,
        });
      }
      return result;
    }));

    // Wait for all batches to complete
    const batchResults = await Promise.all(batchPromises);

    // Check for cancellation after batches complete
    if (cancelledRef?.current) {
      logger.log('Trade set summary fetch cancelled after batch completion');
      return;
    }

    // Filter out null/invalid results
    const validBatches = batchResults.filter((batch) => batch && batch.property);

    if (validBatches.length === 0) {
      logger.log('No valid batch results returned');
      if (onDataReceived) {
        onDataReceived(initialData, []);
      }
      return;
    }

    // Merge all batches into final result
    const mergedData = mergePropertyDataBatches(initialData, validBatches);

    // Stage 6 (preparing data and rendering) is handled by MARK_FULLY_LOADED
    // in the hook's onDataReceived callback

    if (onDataReceived) {
      onDataReceived(mergedData, validBatches);
    }
  } catch (error) {
    logger.error('Error fetching trade set summary:', error);
    if (onError) {
      onError(error, 'Error fetching trade set summary data');
    }
  }
};

/**
 * Fetch time series data (consumed, generated, trade summary) for a given time range
 * Handles batching of trade summary data based on historian aggregation and rules count
 * Used when dateSpan changes but meters/rules/property data remain the same
 * Batch size is calculated internally based on historian aggregation and rules count
 *
 * Loading state is managed via stateDispatchFunc which dispatches actions to the reducer.
 * @param {object} args - Arguments for the fetch operation
 * @param {Array} args.meters - Pre-fetched meters array
 * @param {Array} args.rulesData - Pre-fetched rules data
 * @param {object} args.dateSpan - Date span with start and finish properties (Luxon DateTimes)
 * @param {string} args.timezone - Timezone to use for data
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {string} args.historianAggregation -
 * Aggregation level for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} args.tradeAggregation - Aggregation level for trade data
 * @param {object} args.initialData - Initial data to merge batches with
 * @param {Function} args.onDataReceived - Callback when all data is received and merged
 * @param {Function} args.onError - Callback when an error occurs
 * @param {Function} args.stateDispatchFunc - Function to dispatch loading state actions
 * @param {object} args.isFetchingRef - Ref to track if fetching is in progress
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 * @param {boolean} [args.subsequentLoad=false] - Whether this is a subsequent load (date range
 * change) vs initial load. When true, dispatches cached meters/rules counts to restore metadata
 * after START_REQUEST resets it.
 */
export const getTimeSeriesData = async (args) => {
  const {
    meters,
    rulesData,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    stateDispatchFunc,
    isFetchingRef,
    cancelledRef,
    subsequentLoad = false,
  } = args;

  // Safety check for required parameters
  if (!meters
    || !rulesData
    || !dateSpan
    || !timezone
    || !environment
    || !historianAggregation
    || !tradeAggregation) {
    if (onError) {
      onError(new Error('Missing required parameters for getTimeDependendData'));
    }
    return;
  }

  // Prevent parallel fetches
  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping time-dependent data fetch - another fetch is already in progress');
    return;
  }

  // Set fetching flag if available
  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    const { start, finish } = dateSpan || {};

    // Calculate total rules count and batch size
    const totalRulesCount = getTotalRulesCount(rulesData);

    // For subsequent loads (date range changes), we need to dispatch the cached
    // meters and rules counts since START_REQUEST resets metadata to defaults.
    // We only do this for subsequent loads to avoid regressing the state machine
    // during initial load when these states have already been properly transitioned.
    if (subsequentLoad && stateDispatchFunc) {
      // Dispatch meters loaded with cached count
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_METERS_LOADED,
        metersCount: meters.length,
      });
      // Dispatch rules loaded with cached count
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_RULES_LOADED,
        rulesCount: totalRulesCount,
      });
    }

    // Check for cancellation before starting data fetch
    if (cancelledRef?.current) {
      logger.log('Time series data fetch cancelled before starting');
      return;
    }

    const batchCalculationResult = calculateTradeSetSummaryBatchSize({
      start,
      finish,
      aggregation: historianAggregation,
      ruleCount: totalRulesCount,
    });

    const timeBatches = generateBatchTimeRanges({
      start,
      finish,
      batchSizeHours: batchCalculationResult.batchSizeHours,
    });

    const batchCount = timeBatches.length;

    // Stages 3 & 4: Fetch consumed and generated data in parallel
    const consumedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_CONSUMED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_CONSUMED_DATA_LOADED });
      }
      return data;
    });

    const generatedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_GENERATED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_GENERATED_DATA_LOADED });
      }
      return data;
    });

    const [consumedData, generatedData] = await Promise.all([
      consumedPromise,
      generatedPromise,
    ]);

    // Check for cancellation after consumed/generated data fetch
    if (cancelledRef?.current) {
      logger.log('Time series data fetch cancelled after consumed/generated data');
      return;
    }

    // Notify stage 5: starting trade set summary
    if (stateDispatchFunc) {
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
        batchesLoaded: 0,
        batchesTotal: batchCount,
      });
    }

    // Stage 5: Fetch trade set summary using time batches
    await getTradeSetSummaries({
      meters,
      rulesData,
      consumedData,
      generatedData,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      onDataReceived,
      onError,
      stateDispatchFunc,
      cancelledRef,
    });
  } catch (error) {
    logger.error('Error fetching time-dependent data:', error);
    if (onError) {
      onError(error, 'Error fetching time-dependent property data');
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};

/**
 * Main function to fetch property data using four-stage approach with batched trade summary
 * Stages 1-4 are fetched once outside batches: meters, rules, consumed data, generated data
 * Stage 5 (trade summary) is fetched per batch
 * Batch size is calculated internally based on historian aggregation and rules count
 *
 * Loading state is managed via stateDispatchFunc which dispatches actions to the reducer.
 * @param {object} args - Arguments for the fetch operation
 * @param {string} args.propertyId - ID of the property to fetch
 * @param {object} args.dateSpan - Date span with start and finish properties (Luxon DateTimes)
 * @param {string} args.timezone - Timezone to use for data
 * @param {object} args.environment - Relay environment for GraphQL queries
 * @param {string} args.historianAggregation - Aggregation level for historian data
 * @param {string} args.tradeAggregation - Aggregation level for trade data
 * @param {object} args.initialData - Initial data to merge batches with
 * @param {Function} args.onDataReceived - Callback when all data is received and merged
 * @param {Function} args.onError - Callback when an error occurs
 * @param {Function} args.stateDispatchFunc - Function to dispatch loading state actions
 * @param {object} args.isFetchingRef - Ref to track if fetching is in progress
 * @param {object} [args.cancelledRef] - Ref to check if request has been cancelled
 */
export const getPropertyDataInBatches = async (args) => {
  const {
    propertyId,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    stateDispatchFunc,
    isFetchingRef,
    cancelledRef,
  } = args;

  // Safety check for required parameters
  if (!propertyId
    || !dateSpan
    || !timezone
    || !environment
    || !historianAggregation
    || !tradeAggregation) {
    if (onError) {
      onError(new Error('Missing required parameters for getPropertyDataInPartition'));
    }
    return;
  }

  // Prevent parallel fetches
  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping three-stage fetch - another fetch is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    const { start, finish } = dateSpan || {};

    // Check for cancellation before starting
    if (cancelledRef?.current) {
      logger.log('Property data fetch cancelled before starting');
      return;
    }

    // Stage 1: Fetch all meters once with pagination
    const meters = await getMeters({
      propertyId,
      environment,
      pageSize: METER_LIST_PAGE_SIZE,
    });

    // Check for cancellation after meters fetch
    if (cancelledRef?.current) {
      logger.log('Property data fetch cancelled after meters fetch');
      return;
    }

    if (meters.length === 0) {
      logger.log('No meters found, returning empty data');
      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: PropertyShowLoadingActions.SET_METERS_LOADED,
          metersCount: 0,
        });
      }
      if (onDataReceived) {
        onDataReceived(initialData, []);
      }
      return;
    }

    // Notify stage 1 complete: meters loaded
    if (stateDispatchFunc) {
      stateDispatchFunc({
        type: PropertyShowLoadingActions.SET_METERS_LOADED,
        metersCount: meters.length,
      });
    }

    // Stages 2, 3, & 4: Fetch rules, consumed, and generated data in parallel
    // All three only depend on the meters list from Stage 1.
    // Each stage dispatches its completion action as soon as it resolves,
    // enabling incremental progress bar updates.
    const rulesPromise = Promise.all(meters.map((meter) => getRules({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      pageSize: TRADE_RULE_LIST_PAGE_SIZE,
    }))).then((data) => {
      if (stateDispatchFunc) {
        stateDispatchFunc({
          type: PropertyShowLoadingActions.SET_RULES_LOADED,
          rulesCount: getTotalRulesCount(data),
        });
      }
      return data;
    });

    const consumedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_CONSUMED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_CONSUMED_DATA_LOADED });
      }
      return data;
    });

    const generatedPromise = Promise.all(meters.map((meter) => getMeterData({
      meterId: meter.uuid,
      start,
      finish,
      environment,
      metric: ELEC_ENERGY_GENERATED,
      historianAggregation,
      timezone,
    }))).then((data) => {
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PropertyShowLoadingActions.SET_GENERATED_DATA_LOADED });
      }
      return data;
    });

    const [rulesData, consumedData, generatedData] = await Promise.all([
      rulesPromise,
      consumedPromise,
      generatedPromise,
    ]);

    // Check for cancellation after rules/consumed/generated fetch
    if (cancelledRef?.current) {
      logger.log('Property data fetch cancelled after rules/consumed/generated fetch');
      return;
    }

    // Build initial data structure with fetched meters and rules
    const dataWithMetersAndRules = mergePropertyDataBatches(initialData, [{
      property: {
        meters: {
          edges: meters.map((meter) => {
            // Match rules to meter by uuid from rulesData array
            const meterRulesData = rulesData.find((r) => r.uuid === meter.uuid);
            // meterRulesData.rules is already { edges: [...] }
            // where edges contains { node: rule } objects
            const meterRules = meterRulesData?.rules || { edges: [] };
            return {
              node: {
                ...meter,
                rules: meterRules,
              },
            };
          }),
        },
      },
    }]);

    // Stage 5: Fetch trade set summary using batched time ranges
    await getTradeSetSummaries({
      meters,
      rulesData,
      consumedData,
      generatedData,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData: dataWithMetersAndRules,
      onDataReceived,
      onError,
      stateDispatchFunc,
      cancelledRef,
    });
  } catch (error) {
    logger.error('Error in property data fetching:', error);
    if (onError) {
      onError(error, 'Error fetching property data. Please try again.');
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};
